import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  ROLE_TEMPLATES, 
  PERMISSION_GROUPS,
} from '@/types/permissions';
import type {
  Permission, 
  EntityRestriction,
  DataAccessLevel,
  DisplayPrecision
} from '@/types/permissions';
import { getDemoRole, saveDemoRole } from '@/utils/demoRolePersistence';
import { RELEASE1_DISABLED_CARDS } from '@/config/dashboardLayouts';
import { wouldRoleUpdateCauseLockout, LOCKOUT_ERROR_MESSAGE } from '@/utils/lockoutProtection';
import type { RoleWithPermissionsResponseApi } from '@/api/models/role';
import { createRole, getRoleWithPermissions, updateRole } from '@/api/services/roleService';
import type { ModeType } from '@/types';
import { ArrowLeft, Info, Loader2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CustomizeRoleProps {
  roleId: string | null;
  onBack: () => void;
  mode: ModeType;
  isCloning?: boolean;
  usersInRole?: { userId: string; name: string; email: string }[];
}

export function CustomizeRole({ roleId, onBack, mode, isCloning = false, usersInRole = [] }: CustomizeRoleProps) {
  const { toast } = useToast();
  const { handleNavigate } = useApp();
  const queryClient = useQueryClient();
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [entityRestriction, setEntityRestriction] = useState<EntityRestriction>('all');
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [dataAccessLevel, setDataAccessLevel] = useState<DataAccessLevel>('consolidated');
  const [displayPrecision, setDisplayPrecision] = useState<DisplayPrecision>('full');
  const [allowUserOverride, setAllowUserOverride] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [roleNameError, setRoleNameError] = useState<string>('');
  const [isExternalRole, setIsExternalRole] = useState(false);
  const [isExternalRoleLocked, setIsExternalRoleLocked] = useState(false);
  const [showExternalRoleFlag, setShowExternalRoleFlag] = useState(true);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [showThumbnails, setShowThumbnails] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('permissions-show-thumbnails');
    return saved !== null ? saved === 'true' : true; // Default to true (checked)
  });
  const [hoveredThumbnail, setHoveredThumbnail] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const [apiRoleData, setApiRoleData] = useState<RoleWithPermissionsResponseApi | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availableEntities = ['Birmingham', 'Manchester', 'Leeds', 'London', 'Bristol'];

  const visiblePermissionGroups = PERMISSION_GROUPS
    .filter(group => mode === 'demo' || !group.demoOnly)
    .map(group => ({
      ...group,
      permissions: group.permissions
        .filter(permission => mode === 'demo' || !permission.demoOnly)
        .map(permission => ({
          ...permission,
          subPermissions: permission.subPermissions?.filter(
            subPermission => mode === 'demo' || !subPermission.demoOnly,
          ),
        })),
    }))
    .filter(group => group.permissions.length > 0);

  const visiblePermissionIds = new Set<Permission>(
    visiblePermissionGroups.flatMap(group =>
      group.permissions.flatMap(permission => [
        permission.id,
        ...(permission.subPermissions?.map(subPermission => subPermission.id) ?? []),
      ]),
    ),
  );

  const visibleSelectedPermissions = selectedPermissions.filter(permission =>
    visiblePermissionIds.has(permission),
  );

  const demoOnlyPermissionIds = new Set<Permission>(
    PERMISSION_GROUPS.flatMap(group =>
      group.permissions.flatMap(permission => {
        const permissionIsDemoOnly = Boolean(group.demoOnly || permission.demoOnly);

        return [
          ...(permissionIsDemoOnly ? [permission.id] : []),
          ...(permission.subPermissions
            ?.filter(subPermission => permissionIsDemoOnly || subPermission.demoOnly)
            .map(subPermission => subPermission.id) ?? []),
        ];
      }),
    ),
  );

  const permissionsToSave = mode === 'demo'
    ? selectedPermissions
    : selectedPermissions.filter(permission => !demoOnlyPermissionIds.has(permission));

  const loadDemoRole = useCallback(() => {
    if (roleId) {
      // Use getDemoRole which handles session persistence
      // On first load: returns role from ROLE_TEMPLATES (with our configured defaults)
      // Within session: returns modified role from sessionStorage
      // On refresh: resets to ROLE_TEMPLATES defaults
      const role = getDemoRole(roleId);
      if (role) {
        setRoleName(isCloning ? '' : role.name);
        setRoleDescription(role.description);
        setSelectedPermissions([...role.permissions]);
        setEntityRestriction(role.entityRestriction);
        setSelectedEntities(role.allowedEntities || []);
        setDataAccessLevel(role.dataAccessLevel || 'consolidated');
        setDisplayPrecision(role.displayPrecision || 'full');
        setAllowUserOverride(role.allowUserOverride || false);
        setIsLocked(isCloning ? false : (role.isLocked || false));
        
        // Handle external role flag
        // User and System Administrator roles: Don't show the flag at all
        if (role.id === 'preparer' || role.id === 'system_admin') {
          setShowExternalRoleFlag(false);
          setIsExternalRole(false);
          setIsExternalRoleLocked(false);
        } else {
          setShowExternalRoleFlag(true);
          // When cloning, inherit the flag state but make it editable
          setIsExternalRole(role.isExternalRole || false);
          setIsExternalRoleLocked(isCloning ? false : (role.isExternalRoleLocked || false));
        }
      }
    } else {
      // Creating new custom role from scratch
      setRoleName('');
      setRoleDescription('');
      setSelectedPermissions([]);
      setEntityRestriction('all');
      setSelectedEntities([]);
      setDisplayPrecision('full');
      setAllowUserOverride(false);
      setIsLocked(false);
      setShowExternalRoleFlag(true);
      setIsExternalRole(false);
      setIsExternalRoleLocked(false);
    }
    setHasChanges(false);
  }, [roleId, isCloning]);

  const loadLiveRole = useCallback(async () => {
    if (!roleId) {
      setRoleName('');
      setRoleDescription('');
      setSelectedPermissions([]);
      setEntityRestriction('all');
      setSelectedEntities([]);
      setDisplayPrecision('full');
      setAllowUserOverride(false);
      setIsLocked(false);
      setShowExternalRoleFlag(true);
      setIsExternalRole(false);
      setIsExternalRoleLocked(false);
      setHasChanges(false);
      return;
    }

    setRoleLoading(true);
    setRoleError(null);
    try {
      const numericRoleId = Number(roleId);
      const roleData = await getRoleWithPermissions(numericRoleId);
      setApiRoleData(roleData);
      setRoleName(isCloning ? '' : roleData.roleName);
      setRoleDescription(roleData.description || '');
      setIsLocked(isCloning ? false : roleData.isSystemRole);

      const enabledPermissions = roleData.permissions
        .filter((p) => p.isEnabled)
        .map((p) => p.permissionKey as Permission);
      setSelectedPermissions(enabledPermissions);

      setEntityRestriction('all');
      setSelectedEntities([]);
      
      // For live mode, show external role flag for all roles except system roles
      // In live mode, we don't have the template data, so we'll show it for all non-system roles
      setShowExternalRoleFlag(!roleData.isSystemRole);
      setIsExternalRole(roleData.isExternalRole || false);
      setIsExternalRoleLocked(false);
    } catch (err) {
      setRoleError(
        err instanceof Error ? err.message : 'Failed to load role details',
      );
    } finally {
      setRoleLoading(false);
      setHasChanges(false);
    }
  }, [roleId, isCloning]);

  useEffect(() => {
    if (mode === 'live') {
      loadLiveRole();
    } else {
      loadDemoRole();
    }
  }, [mode, loadLiveRole, loadDemoRole]);

  // Helper function to check if a permission is an "Edit" or action-based permission
  const isEditPermission = (permission: Permission): boolean => {
    const actionKeywords = [
      'edit', 'create', 'delete', 'adjust', 'mark', 'manage', 'configure', 
      'schedule', 'approve', 'chase', 'execute', 'perform', 'categorise',
      'interact', 'generate', 'share', 'export'
    ];
    const permissionLower = permission.toLowerCase();
    return actionKeywords.some(keyword => permissionLower.includes(keyword));
  };

  // Counts selected permissions visible in the current mode
  const getPermissionCount = (): number => {
    return visibleSelectedPermissions.length;
  };

  const togglePermission = (permission: Permission, parentPermission?: Permission) => {
    // If external role is enabled and this is an edit permission, don't allow toggling
    if (isExternalRole && isEditPermission(permission)) {
      return;
    }
    
    setHasChanges(true);
    setSelectedPermissions(prev => {
      if (prev.includes(permission)) {
        // Unchecking a permission
        // If it's a parent permission, also uncheck all its children
        const permissionItem = PERMISSION_GROUPS
          .flatMap(g => g.permissions)
          .find(p => p.id === permission);
        
        if (permissionItem?.subPermissions) {
          const childIds = permissionItem.subPermissions.map(sp => sp.id);
          return prev.filter(p => p !== permission && !childIds.includes(p));
        }
        
        return prev.filter(p => p !== permission);
      } else {
        // Checking a permission
        // If it's a child permission, ensure parent is also checked
        if (parentPermission && !prev.includes(parentPermission)) {
          return [...prev, parentPermission, permission];
        }
        return [...prev, permission];
      }
    });
  };

  const handleEntityRestrictionChange = (value: EntityRestriction) => {
    setHasChanges(true);
    setEntityRestriction(value);
    if (value !== 'specific') {
      setSelectedEntities([]);
    }
  };

  const handleDataAccessLevelChange = (value: DataAccessLevel) => {
    setHasChanges(true);
    setDataAccessLevel(value);
  };

  const handleDisplayPrecisionChange = (value: DisplayPrecision) => {
    setHasChanges(true);
    setDisplayPrecision(value);
  };

  const handleAllowUserOverrideChange = (checked: boolean) => {
    setHasChanges(true);
    setAllowUserOverride(checked);
  };

  const handleExternalRoleChange = (checked: boolean) => {
    setHasChanges(true);
    setIsExternalRole(checked);
    
    // If enabling external role, remove all edit permissions
    if (checked) {
      setSelectedPermissions(prev => prev.filter(p => !isEditPermission(p)));
    }
  };

  const handleThumbnailToggle = (checked: boolean) => {
    setShowThumbnails(checked);
    // Persist preference to localStorage
    localStorage.setItem('permissions-show-thumbnails', String(checked));
  };

  const handleSaveAsNew = async () => {
    if (!roleName.trim()) {
      setRoleNameError('Role name is required');
      toast({
        title: 'Error',
        description: 'Please enter a role name',
        variant: 'destructive',
      });
      return;
    }
    setRoleNameError('');

    if (visibleSelectedPermissions.length === 0) {
      toast({
        title: 'Error',
        description: 'At least one permission must be selected',
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'live') {
      setSaving(true);
      try {
        const permissionKeys = permissionsToSave.map((p) => p as string);
        await createRole({
          name: roleName,
          description: roleDescription || null,
          permissionKeys,
          isExternalRole,
        });
        toast({
          title: 'Role Created',
          description: `New role "${roleName}" has been created successfully.`,
        });
        onBack();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to create role',
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    const newDemoRoleId = `custom_${Date.now()}`;
    saveDemoRole({
      id: newDemoRoleId,
      name: roleName,
      description: roleDescription,
      permissions: selectedPermissions,
      entityRestriction,
      allowedEntities: selectedEntities,
      dataAccessLevel,
      displayPrecision,
      allowUserOverride,
      userCount: 0,
      isTemplate: false,
      isLocked: false,
      isExternalRole,
      isExternalRoleLocked: false,
    });
    toast({
      title: 'Role Created',
      description: `New role "${roleName}" has been created successfully.`,
    });
    onBack();
  };

  const handleUpdate = async () => {
    if (!roleName.trim()) {
      setRoleNameError('Role name is required');
      toast({
        title: 'Error',
        description: 'Please enter a role name',
        variant: 'destructive',
      });
      return;
    }
    setRoleNameError('');

    if (mode === 'live' && roleId) {
      setSaving(true);
      try {
        const permissionKeys = permissionsToSave.map((p) => p as string);
        await updateRole(Number(roleId), {
          name: roleName,
          description: roleDescription || null,
          permissionKeys,
          isExternalRole,
        });
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        toast({
          title: 'Role Updated',
          description: 'Changes apply immediately to all users with this role.',
        });
        onBack();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to update role',
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Demo mode - save to in-memory cache (no confirmation needed in demo mode)
      console.log('Saving role in demo mode:', roleName);
      
      if (roleId) {
        // TRIGGER 1: Check for lockout protection before saving
        const updatedRole = {
          id: roleId,
          name: roleName,
          description: roleDescription,
          permissions: selectedPermissions,
          entityRestriction,
          allowedEntities: selectedEntities,
          dataAccessLevel,
          displayPrecision,
          allowUserOverride,
          userCount: usersInRole.length,
          isTemplate: true,
          isLocked,
          isExternalRole,
          isExternalRoleLocked,
        };
        
        if (wouldRoleUpdateCauseLockout(updatedRole, mode)) {
          toast({
            title: 'Cannot Save Changes',
            description: LOCKOUT_ERROR_MESSAGE,
            variant: 'destructive',
          });
          return;
        }
        
        saveDemoRole(updatedRole);
        
        console.log('Role saved successfully in demo mode');
      }

      toast({
        title: 'Role Updated',
        description: 'Changes apply immediately to all users with this role.',
      });
      onBack();
    }
  };

  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const requestNavigation = (navigate: () => void) => {
    if (hasChanges) {
      setPendingNavigation(() => navigate);
      return;
    }
    navigate();
  };

  const handleConfirmDiscard = () => {
    const navigate = pendingNavigation;
    setPendingNavigation(null);
    navigate?.();
  };

  const handleCancel = () => {
    requestNavigation(onBack);
  };

  const handleViewAllUsers = () => {
    requestNavigation(() => handleNavigate('admin-users', { roleFilter: roleName }));
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isR1HiddenPermission = (permissionId: Permission) =>
    mode === 'live' && RELEASE1_DISABLED_CARDS.has(permissionId);

  const getEnabledCount = (groupPermissions: typeof PERMISSION_GROUPS[0]['permissions']) => {
    let count = 0;
    groupPermissions.forEach(perm => {
      if (isR1HiddenPermission(perm.id)) {
        return;
      }
      if (selectedPermissions.includes(perm.id)) {
        count++;
      }
      if (perm.subPermissions) {
        perm.subPermissions.forEach(subPerm => {
          if (selectedPermissions.includes(subPerm.id)) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const getTotalCount = (groupPermissions: typeof PERMISSION_GROUPS[0]['permissions']) => {
    let count = 0;
    groupPermissions.forEach(perm => {
      if (isR1HiddenPermission(perm.id)) {
        return;
      }
      count++;
      if (perm.subPermissions) {
        count += perm.subPermissions.length;
      }
    });
    return count;
  };

  // Filter permissions based on Release 1 availability and search
  const filteredPermissionGroups = visiblePermissionGroups.map(group => {
    // Always hide Release-1-disabled dashboard-card permissions in live mode.
    const basePermissions = group.permissions.filter(perm => !isR1HiddenPermission(perm.id));

    if (!permissionSearch.trim()) {
      return {
        ...group,
        permissions: basePermissions
      };
    }
    
    const searchLower = permissionSearch.toLowerCase();
    const filteredPermissions = basePermissions.filter(perm => {
      // Check if parent matches
      const parentMatches = perm.label.toLowerCase().includes(searchLower) ||
        (perm.description && perm.description.toLowerCase().includes(searchLower));
      
      // Check if any sub-permission matches
      const childMatches = perm.subPermissions?.some(sub =>
        sub.label.toLowerCase().includes(searchLower) ||
        (sub.description && sub.description.toLowerCase().includes(searchLower))
      );
      
      return parentMatches || childMatches;
    });
    
    return {
      ...group,
      permissions: filteredPermissions
    };
  }).filter(group => group.permissions.length > 0);

  const sampleUsers = usersInRole.slice(0, 3);
  const remainingCount = usersInRole.length - 3;

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading role details...</span>
      </div>
    );
  }

  if (roleError && mode === 'live') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Roles
          </Button>
        </div>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Role</CardTitle>
            <CardDescription>{roleError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={loadLiveRole}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Roles
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Details</CardTitle>
              <CardDescription>Basic information about this role</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="roleName">
                  Role Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="roleName"
                  value={roleName}
                  onChange={(e) => {
                    setRoleName(e.target.value);
                    setHasChanges(true);
                    if (roleNameError) setRoleNameError('');
                  }}
                  placeholder="e.g., Senior Analyst"
                  disabled={isLocked}
                  className={roleNameError ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {roleNameError && (
                  <p className="text-sm text-destructive">{roleNameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="roleDescription">Description</Label>
                <Input
                  id="roleDescription"
                  value={roleDescription}
                  onChange={(e) => {
                    setRoleDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  placeholder="Brief description of this role's purpose"
                  disabled={isLocked}
                />
              </div>
              
              {showExternalRoleFlag && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="externalRole"
                      checked={isExternalRole}
                      onCheckedChange={handleExternalRoleChange}
                      disabled={isLocked || isExternalRoleLocked}
                    />
                    <div className="grid gap-1 leading-none">
                      <label
                        htmlFor="externalRole"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-2"
                      >
                        External user role
                        {isExternalRoleLocked && (
                          <span className="text-muted-foreground">🔒</span>
                        )}
                      </label>
                      <p className="text-sm text-muted-foreground">
                        {isExternalRoleLocked
                          ? 'This role is permanently view-only for data protection. Edit permissions are always disabled.'
                          : 'When enabled, all "Edit" permissions are disabled. User has view-only access only.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
              <CardDescription>
                Select which actions this role can perform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Search Box */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search permissions..."
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Thumbnail Display Toggle */}
              <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id="showThumbnails"
                  checked={showThumbnails}
                  onCheckedChange={handleThumbnailToggle}
                />
                <div className="grid gap-1 leading-none">
                  <label
                    htmlFor="showThumbnails"
                    className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                  >
                    Display thumbnails
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Uncheck to show thumbnails on hover only
                  </p>
                </div>
              </div>

              {isExternalRole && (
                <Alert>
                  <AlertDescription>
                    Edit permissions disabled - this is a view-only role
                  </AlertDescription>
                </Alert>
              )}
              
              {filteredPermissionGroups.length === 0 && permissionSearch && (
                <div className="text-center py-8 text-muted-foreground">
                  No permissions found matching "{permissionSearch}"
                </div>
              )}
              
              {filteredPermissionGroups.map((group) => {
                const isCollapsed = collapsedSections[group.id];
                const enabledCount = getEnabledCount(group.permissions);
                const totalCount = getTotalCount(group.permissions);
                
                return (
                  <div key={group.id} className="space-y-3">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-md -ml-2"
                      onClick={() => toggleSection(group.id)}
                    >
                      <div className="flex items-center gap-2">
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        <h3 className="font-semibold text-sm">{group.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {enabledCount} of {totalCount} enabled
                        </Badge>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="space-y-2 pl-2">
                    {group.permissions.map((perm) => {
                      const isEdit = isEditPermission(perm.id);
                      const isR1Disabled = RELEASE1_DISABLED_CARDS.has(perm.id);
                      const isDisabled = isLocked || (isExternalRole && isEdit) || isR1Disabled;
                      const hasSubPermissions = perm.subPermissions && perm.subPermissions.length > 0;
                      const isParentChecked = selectedPermissions.includes(perm.id);
                      
                      return (
                        <div key={perm.id} className="space-y-2">
                          {/* Parent Permission */}
                          <div 
                            className="flex items-start space-x-3"
                            onMouseEnter={() => perm.thumbnail && setHoveredThumbnail(perm.id)}
                            onMouseLeave={() => perm.thumbnail && setHoveredThumbnail(null)}
                          >
                            <Checkbox
                              id={perm.id}
                              checked={isParentChecked}
                              onCheckedChange={() => togglePermission(perm.id)}
                              disabled={isDisabled}
                            />
                            <div className="grid gap-1 leading-none flex-1">
                              <label
                                htmlFor={perm.id}
                                className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer ${
                                  isExternalRole && isEdit ? 'text-muted-foreground line-through' : ''
                                }`}
                              >
                                {perm.label}
                                {isR1Disabled && (
                                  <span className="ml-2 text-xs font-normal text-muted-foreground">(not available in Release 1)</span>
                                )}
                              </label>
                              {perm.description && (
                                <p className="text-sm text-muted-foreground">
                                  {perm.description}
                                </p>
                              )}
                            </div>
                            {/* Thumbnail - reserve space only when always visible */}
                            {perm.thumbnail && (
                              <div className={`flex-shrink-0 relative overflow-visible ${showThumbnails ? 'w-[120px] h-[80px]' : 'w-0 h-0'}`}>
                                {(showThumbnails || hoveredThumbnail === perm.id) && (
                                  <div className={`absolute top-0 right-0 ${hoveredThumbnail === perm.id ? 'z-50 drop-shadow-lg' : 'z-0'}`}>
                                    <perm.thumbnail 
                                      isHovered={hoveredThumbnail === perm.id} 
                                      alwaysVisible={showThumbnails}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Sub-Permissions (nested/indented) */}
                          {hasSubPermissions && (
                            <div className="ml-8 space-y-2 border-l-2 border-muted pl-4">
                              {perm.subPermissions!.map((subPerm) => {
                                const isSubEdit = isEditPermission(subPerm.id);
                                const isSubDisabled = isLocked || !isParentChecked || (isExternalRole && isSubEdit);
                                
                                return (
                                  <div key={subPerm.id} className="flex items-start space-x-3">
                                    <Checkbox
                                      id={subPerm.id}
                                      checked={selectedPermissions.includes(subPerm.id)}
                                      onCheckedChange={() => togglePermission(subPerm.id, perm.id)}
                                      disabled={isSubDisabled}
                                    />
                                    <div className="grid gap-1 leading-none">
                                      <label
                                        htmlFor={subPerm.id}
                                        className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer ${
                                          isExternalRole && isSubEdit ? 'text-muted-foreground line-through' : ''
                                        } ${!isParentChecked ? 'opacity-50' : ''}`}
                                      >
                                        {subPerm.label}
                                      </label>
                                      {subPerm.description && (
                                        <p className="text-sm text-muted-foreground">
                                          {subPerm.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                      </div>
                    )}
                    {group.id !== 'administration' && <Separator />}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {mode === 'demo' && (
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => toggleSection('entity_restrictions')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {collapsedSections['entity_restrictions'] ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Entity/Location Restrictions
                  </CardTitle>
                  <CardDescription>
                    Control which entities or locations this role can access
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            {!collapsedSections['entity_restrictions'] && (
              <CardContent className="space-y-4">
              <RadioGroup value={entityRestriction} onValueChange={(value) => !isLocked && handleEntityRestrictionChange(value as EntityRestriction)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" disabled={isLocked} />
                  <Label htmlFor="all" className="cursor-pointer">
                    Access all entities/locations
                  </Label>
                </div>
                <div className="flex items-center space-x-2 opacity-40">
                  <RadioGroupItem value="specific" id="specific" disabled={true} />
                  <Label htmlFor="specific" className="cursor-not-allowed">
                    Restrict to specific entities
                    <span className="ml-2 text-xs font-normal text-muted-foreground">(not available in Release 1)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 opacity-40">
                  <RadioGroupItem value="own" id="own" disabled={true} />
                  <Label htmlFor="own" className="cursor-not-allowed">
                    Restrict to user's assigned entity only
                    <span className="ml-2 text-xs font-normal text-muted-foreground">(not available in Release 1)</span>
                  </Label>
                </div>
              </RadioGroup>

              {entityRestriction === 'specific' && (
                <div className="space-y-2 pl-6">
                  <Label>Select Entities</Label>
                  <Select
                    value={selectedEntities[0] || ''}
                    onValueChange={(value) => {
                      setSelectedEntities([value]);
                      setHasChanges(true);
                    }}
                    disabled={isLocked}
                  >
                    <SelectTrigger disabled={isLocked}>
                      <SelectValue placeholder="Select entities..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableEntities.map((entity) => (
                        <SelectItem key={entity} value={entity}>
                          {entity}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedEntities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedEntities.map((entity) => (
                        <Badge key={entity} variant="secondary">
                          {entity}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </CardContent>
            )}
          </Card>
          )}

          {mode === 'demo' && (
          <>
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => toggleSection('data_access_level')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {collapsedSections['data_access_level'] ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Data Access Level
                  </CardTitle>
              <CardDescription>
                Control the level of detail this role can see in reports and dashboards
              </CardDescription>
                </div>
              </div>
            </CardHeader>
            {!collapsedSections['data_access_level'] && (
              <CardContent className="space-y-4">
              <RadioGroup value={dataAccessLevel} onValueChange={(value) => !isLocked && handleDataAccessLevelChange(value as DataAccessLevel)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="full" id="full" disabled={isLocked} />
                  <Label htmlFor="full" className="cursor-pointer">
                    Full detail with drill-down
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consolidated" id="consolidated" disabled={isLocked} />
                  <Label htmlFor="consolidated" className="cursor-pointer">
                    Consolidated totals only
                  </Label>
                </div>
              </RadioGroup>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => toggleSection('display_precision')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {collapsedSections['display_precision'] ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Display Precision
                  </CardTitle>
              <CardDescription>
                Control how financial amounts are displayed throughout the application
              </CardDescription>
                </div>
              </div>
            </CardHeader>
            {!collapsedSections['display_precision'] && (
              <CardContent className="space-y-4">
              <RadioGroup value={displayPrecision} onValueChange={(value) => !isLocked && handleDisplayPrecisionChange(value as DisplayPrecision)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="full" id="precision-full" disabled={isLocked} />
                  <Label htmlFor="precision-full" className="cursor-pointer">
                    <div className="flex flex-col">
                      <span className="font-medium">Full precision</span>
                      <span className="text-sm text-muted-foreground">Example: £247,356.22</span>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="rounded" id="precision-rounded" disabled={isLocked} />
                  <Label htmlFor="precision-rounded" className="cursor-pointer">
                    <div className="flex flex-col">
                      <span className="font-medium">Rounded</span>
                      <span className="text-sm text-muted-foreground">Example: £247,000</span>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="abbreviated" id="precision-abbreviated" disabled={isLocked} />
                  <Label htmlFor="precision-abbreviated" className="cursor-pointer">
                    <div className="flex flex-col">
                      <span className="font-medium">Abbreviated</span>
                      <span className="text-sm text-muted-foreground">Example: £247K</span>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="millions" id="precision-millions" disabled={isLocked} />
                  <Label htmlFor="precision-millions" className="cursor-pointer">
                    <div className="flex flex-col">
                      <span className="font-medium">Millions</span>
                      <span className="text-sm text-muted-foreground">Example: £0.25M</span>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              <Separator className="my-4" />

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="allow-user-override" 
                  checked={allowUserOverride}
                  onCheckedChange={handleAllowUserOverrideChange}
                  disabled={isLocked}
                />
                <Label htmlFor="allow-user-override" className="cursor-pointer text-sm">
                  Allow user to set their own display precision
                </Label>
              </div>
              </CardContent>
            )}
          </Card>
          </>
          )}
        </div>

        <div className="space-y-6">
          {roleId && usersInRole.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Users With This Role</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Users with this role: <span className="font-semibold">{usersInRole.length}</span>
                  </p>
                  {sampleUsers.length > 0 && (
                    <div className="space-y-2">
                      {sampleUsers.map((user) => (
                        <div key={user.userId} className="text-sm">
                          {user.name}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div className="text-sm text-muted-foreground">
                          +{remainingCount} more
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={handleViewAllUsers}
                  >
                    View All Users
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                Permission Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Permissions:</span>
                  <span className="font-semibold">{getPermissionCount()}</span>
                </div>
                {mode === 'demo' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entity Access:</span>
                    <span className="font-semibold capitalize">{entityRestriction}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {hasChanges && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Don't forget to save before leaving.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={handleCancel}>
          {isLocked ? 'Close' : 'Cancel'}
        </Button>
        {!isLocked && (
          <>
            {roleId && !isCloning ? (
              <Button onClick={handleUpdate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Update Role
              </Button>
            ) : (
              <Button onClick={handleSaveAsNew} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save as New Role
              </Button>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={pendingNavigation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingNavigation(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this role. If you leave now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
